package one.healthium.insights.ui.personal_mealplan

import co.touchlab.kermit.Logger
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.withIndex
import one.healthium.base.preferences.AppPreferences
import one.healthium.channels.MealPlanChannel
import one.healthium.insights.domain.content.ContentRepository
import one.healthium.insights.domain.models.MealPlanResultModel
import one.healthium.insights.domain.models.RecipeModel
import one.healthium.ui.common.vm.BaseViewModel

internal class PersonalMealPlanViewModel(
    private val contentRepository: ContentRepository,
    private val melPlanChannel: MealPlanChannel,
    private val appPreferences: AppPreferences,
) : BaseViewModel() {

    private val _state = MutableStateFlow(
        PersonalMealPlanUiState(
            selectedDayIndex = appPreferences.mealPlanSelectedDayIndex,
        )
    )

    fun state(): StateFlow<PersonalMealPlanUiState> = _state.asStateFlow()

    init {
        appPreferences.isMetricFlow
            .onEach { isMetrics ->
                _state.update { state -> state.copy(isMetrics = isMetrics) }
            }
            .launch()

        melPlanChannel.mealPlanRefreshFlow
            .withIndex()
            .flatMapLatest { indexed ->
                _state.update { state -> state.copy(loading = true) }
                contentRepository.getPersonalMealPlan(force = indexed.index > 0)
                    .catch { e ->
                        Logger.e(e) { "Error loading personal meal plan" }
                        if (e !is CancellationException) {
                            emit(
                                MealPlanResultModel.Error(
                                    message = e.message ?: "Unknown error",
                                    action = "retry",
                                )
                            )
                        }
                    }
            }
            .onEach { result ->
                val previousResult = _state.value.result
                val isNewMealPlan = result is MealPlanResultModel.Ready && previousResult !is MealPlanResultModel.Ready
                if (isNewMealPlan) {
                    appPreferences.mealPlanSelectedDayIndex = 0
                }
                _state.update { state ->
                    state.copy(
                        loading = false,
                        result = result,
                        selectedDayIndex = if (isNewMealPlan) 0 else state.selectedDayIndex,
                    )
                }
            }
            .launch()
    }

    fun retry() {
        _state.update { it.copy(loading = true, result = null) }
        melPlanChannel.refreshMealPlan()
    }

    fun selectDay(index: Int) {
        appPreferences.mealPlanSelectedDayIndex = index
        _state.update { state -> state.copy(selectedDayIndex = index) }
    }

    fun onRecipeClicked(recipe: RecipeModel, mealType: String) {
        _state.update { it.copy(selectedRecipe = SelectedRecipe(recipe, mealType)) }
    }

    fun dismissRecipe() {
        _state.update { it.copy(selectedRecipe = null) }
    }
}
